import { useEffect, useMemo, useState } from "react";
import { loginRequest } from "./authClient";
import { useMsal } from "@azure/msal-react";
import env from "../env";

const {
  REACT_APP_ROLE_CLIENT_SUCCESS_MANAGER,
  REACT_APP_ROLE_CLIENT_SUCCESS_OPS,
  REACT_APP_ROLE_COACH,
  REACT_APP_ROLE_SCHOLARSHIP,
  REACT_APP_ROLE_CONTENT,
  REACT_APP_ROLE_ADMIN,
} = env;

function userHasGroup(groups) {
  return function (checkGroup) {
    if (Array.isArray(groups)) {
      return groups.some((group) => group === checkGroup);
    }
    return false;
  };
}

const useAuth = () => {
  const { instance, accounts, inProgress } = useMsal();
  const [token, setToken] = useState();
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);

  useEffect(() => {
    if (inProgress === "none" && accounts.length > 0) {
      setIsLoadingRoles(true);
      instance
        .acquireTokenSilent({
          account: accounts[0],
          scopes: loginRequest.scopes,
        })
        .then((response) => {
          setToken(response);
        })
        .catch((errorMessage) => {
          console.error(errorMessage);
        })
        .finally(() => {
          setIsLoadingRoles(false);
        });
    }

    if (inProgress === "none" && accounts.length === 0) {
      setIsLoadingRoles(false);
    }
  }, [inProgress, accounts, instance]);

  const groups = useMemo(
    () => (token && token.idTokenClaims && token.idTokenClaims.groups) || [],
    [token]
  );

  const roleGroups = useMemo(
    () => ({
      clientSuccessManager: REACT_APP_ROLE_CLIENT_SUCCESS_MANAGER,
      clientSuccessOps: REACT_APP_ROLE_CLIENT_SUCCESS_OPS,
      coach: REACT_APP_ROLE_COACH,
      scholarship: REACT_APP_ROLE_SCHOLARSHIP,
      content: REACT_APP_ROLE_CONTENT,
      admin: REACT_APP_ROLE_ADMIN,
    }),
    []
  );

  const userIs = userHasGroup(groups);

  const hasRole = (roleKey) => {
    const groupId = roleGroups[roleKey];
    if (!groupId) {
      return false;
    }
    return userIs(groupId);
  };

  const hasAnyRole = (roleKeys = []) =>
    Array.isArray(roleKeys) && roleKeys.some((roleKey) => hasRole(roleKey));

  const hasEveryRole = (roleKeys = []) =>
    Array.isArray(roleKeys) && roleKeys.every((roleKey) => hasRole(roleKey));

  return {
    token,
    groups,
    isLoadingRoles,
    hasRole,
    hasAnyRole,
    hasEveryRole,
    isClientSuccessManager: hasRole("clientSuccessManager"),
    isClientSuccessOps: hasRole("clientSuccessOps"),
    isCoach: hasRole("coach"),
    isScholarshipTeam: hasRole("scholarship"),
    isContentTeam: hasRole("content"),
    isAdmin: hasRole("admin"),
  };
};

export default useAuth;
